import type {
  AiPrompt,
  AiPromptContext,
  AiPromptContextIntegration,
  AiPromptContextMention,
  AiPromptContextReference,
  Document,
  ReferenceId,
} from "@ctrl/controller-rpc";

/**
 * Functions for loading agent templates from prompt files.
 * Prompt building is now handled by the OCaml controller.
 */

import type { CatalogKey } from "@/components/ai/useAgents";
import { parseSerializedDocument } from "@/components/document/parse";
import { isAiIntegrationKey } from "@/components/integrations/aiIntegrationKeys";
import { isMcpIntegrationKey } from "@/components/integrations/mcpCandidates";
import { isReferenceKind } from "@/references/kinds";
import { NANOID_LENGTH, nanoidOfPrefixedId } from "@/utils/nanoid";
import { hasOnlyKeys, isPlainRecord } from "@/utils/unknown";

function parseDenseArray<T>(
  value: unknown,
  parse: (item: unknown) => T | null
): T[] | null {
  if (!Array.isArray(value)) return null;
  const parsed: T[] = [];
  for (let index = 0; index < value.length; index += 1) {
    if (!Object.hasOwn(value, index)) return null;
    const item = parse(value[index]);
    if (item === null) return null;
    parsed.push(item);
  }
  return parsed;
}

function parsePromptContextIntegration(
  value: unknown
): AiPromptContextIntegration | null {
  if (
    !isPlainRecord(value) ||
    !hasOnlyKeys(value, ["key", "data", "org_id"]) ||
    (!isAiIntegrationKey(value.key) && !isMcpIntegrationKey(value.key)) ||
    typeof value.data !== "string"
  ) {
    return null;
  }
  // Absent on contexts stored before connections carried their owning
  // organization; the controller falls back to the owner org for those.
  const orgId = value.org_id;
  if (orgId != null && typeof orgId !== "string") return null;
  return orgId == null
    ? { key: value.key, data: value.data }
    : { key: value.key, data: value.data, org_id: orgId };
}

function parsePromptContextMention(
  value: unknown
): AiPromptContextMention | null {
  if (
    !isPlainRecord(value) ||
    !hasOnlyKeys(value, ["key", "data"]) ||
    typeof value.key !== "string" ||
    typeof value.data !== "string"
  ) {
    return null;
  }
  return { key: value.key, data: value.data };
}

function parsePromptContextReference(
  value: unknown
): AiPromptContextReference | null {
  if (
    !isPlainRecord(value) ||
    !hasOnlyKeys(value, ["key", "data"]) ||
    typeof value.key !== "string" ||
    !isPlainRecord(value.data) ||
    !hasOnlyKeys(value.data, ["kind", "id"]) ||
    !isReferenceKind(value.data.kind) ||
    typeof value.data.id !== "string"
  ) {
    return null;
  }
  return {
    key: value.key,
    data: { kind: value.data.kind, id: value.data.id },
  };
}

function parseOptionalContextArray<T>(
  context: Record<string, unknown>,
  key: string,
  parse: (item: unknown) => T | null
): T[] | undefined | null {
  const value = context[key];
  return value === undefined ? undefined : parseDenseArray(value, parse);
}

function parseSerializedPromptContext(value: unknown): AiPromptContext | null {
  if (
    !isPlainRecord(value) ||
    !hasOnlyKeys(value, ["integrations", "mentions", "references"])
  ) {
    return null;
  }
  const integrations = parseOptionalContextArray(
    value,
    "integrations",
    parsePromptContextIntegration
  );
  const mentions = parseOptionalContextArray(
    value,
    "mentions",
    parsePromptContextMention
  );
  const references = parseOptionalContextArray(
    value,
    "references",
    parsePromptContextReference
  );
  if (integrations === null || mentions === null || references === null) {
    return null;
  }
  return {
    ...(integrations === undefined ? {} : { integrations }),
    ...(mentions === undefined ? {} : { mentions }),
    ...(references === undefined ? {} : { references }),
  };
}

function isCompoundId(
  value: string,
  prefix: string,
  nanoidParts: number
): boolean {
  const [actualPrefix, ...parts] = value.split(":");
  return (
    actualPrefix === prefix &&
    parts.length === nanoidParts &&
    parts.every((part) => part.length === NANOID_LENGTH)
  );
}

function isAgentReferenceId(
  value: string,
  prefix: "agent_ref" | "conversation_ref",
  trailingNanoidParts: number
): boolean {
  const [actualPrefix, orgId, agentKind, agentId, ...trailingParts] =
    value.split(":");
  return (
    actualPrefix === prefix &&
    orgId.length === NANOID_LENGTH &&
    (agentKind === "agent" || agentKind === "assistant") &&
    agentId.length === NANOID_LENGTH &&
    trailingParts.length === trailingNanoidParts &&
    trailingParts.every((part) => part.length === NANOID_LENGTH)
  );
}

function isCanonicalEventId(value: string): boolean {
  if (nanoidOfPrefixedId("event", value)) return true;
  const [prefix, nanoid, date, ...extra] = value.split(":");
  if (
    prefix !== "event" ||
    nanoid?.length !== NANOID_LENGTH ||
    !date ||
    extra.length > 0
  ) {
    return false;
  }
  const parsed = new Date(`${date}T00:00:00.000Z`);
  return (
    !Number.isNaN(parsed.getTime()) &&
    parsed.toISOString().slice(0, 10) === date
  );
}

function isCanonicalReferenceId(reference: ReferenceId): boolean {
  switch (reference.kind) {
    case "event":
      return isCanonicalEventId(reference.id);
    case "event-rec":
    case "page":
    case "people":
    case "task-rec":
      return nanoidOfPrefixedId(reference.kind, reference.id) !== null;
    case "task":
      return (
        nanoidOfPrefixedId("task", reference.id) !== null ||
        isCompoundId(reference.id, "object", 2) ||
        isCompoundId(reference.id, "object", 3)
      );
    case "object":
      return (
        isCompoundId(reference.id, "object", 2) ||
        isCompoundId(reference.id, "object", 3)
      );
    case "organization":
      return nanoidOfPrefixedId("org", reference.id) !== null;
    case "table_ref":
    case "workspace_ref":
      return isCompoundId(reference.id, reference.kind, 2);
    case "agent":
      return isAgentReferenceId(reference.id, "agent_ref", 0);
    case "automation":
      return isCompoundId(reference.id, "automation_ref", 2);
    case "conversation":
      return isAgentReferenceId(reference.id, "conversation_ref", 1);
    case "automation_run":
      return isCompoundId(reference.id, "automation_run_ref", 3);
  }
}

function hasCanonicalPromptBlockIds(document: Document): boolean {
  return document.blocks.every((block) => {
    if (!nanoidOfPrefixedId("block", block.id)) return false;
    switch (block.type) {
      case "object":
        if ("referee" in block) {
          return isCanonicalReferenceId(block.referee);
        }
        return (
          nanoidOfPrefixedId("table", block.table_id) !== null &&
          nanoidOfPrefixedId("row", block.row_id) !== null &&
          (block.workspace_id === undefined ||
            nanoidOfPrefixedId("workspace", block.workspace_id) !== null)
        );
      case "recurrent_task":
        return (
          block.recurrent_task_id === undefined ||
          nanoidOfPrefixedId("task-rec", block.recurrent_task_id) !== null
        );
      case "todo":
        return block.task === undefined || block.task.length >= NANOID_LENGTH;
      default:
        return true;
    }
  });
}

/** Parse the closed `AiPrompt` shape accepted by the controller RPC schema. */
export function parseSerializedAiPrompt(
  value: unknown,
  makeBlockId?: () => string
): AiPrompt | null {
  if (!isPlainRecord(value) || !hasOnlyKeys(value, ["document", "context"])) {
    return null;
  }
  const document = parseSerializedDocument(value.document, makeBlockId);
  if (!document || !hasCanonicalPromptBlockIds(document)) return null;
  if (value.context === undefined) return { document };
  const context = parseSerializedPromptContext(value.context);
  if (
    !context ||
    context.mentions?.some(
      (mention) => !nanoidOfPrefixedId("account", mention.data)
    ) ||
    context.references?.some(
      (reference) => !isCanonicalReferenceId(reference.data)
    )
  ) {
    return null;
  }
  return { document, context };
}

/** Known agent template ids (catalog keys) with corresponding .prompt files. */
export const AGENT_TEMPLATE_IDS = [
  "meeting-assistant",
  "operations-manager",
  "social-media-specialist",
  "lead-generator",
  "community-manager",
  "executive-assistant",
  "content-writer",
  "customer-support",
  "project-manager",
] as const;

// Agent instruction templates (eager-loaded). Keys are paths without ?raw.
const agentTemplates = import.meta.glob<string>("./prompts/*.prompt", {
  query: "?raw",
  import: "default",
  eager: true,
});

/*
 * Get the agent template for a given agent identifier.
 * Returns empty string if no template file exists.
 */
export function getAgentTemplate(agentId: CatalogKey): string {
  const key = `./prompts/${agentId}.prompt`;
  const content = agentTemplates[key];
  if (content == null) return "";
  return (typeof content === "string" ? content : "").trim();
}

/**
 * Replace placeholder keys in instruction text with values (literal substring
 * replace). Keys are applied longest-first so a longer token wins over a
 * shorter prefix (e.g. `ORGANIZATION_ID` before `ORG`).
 */
export function applyAgentInstructionPlaceholders(
  text: string,
  placeholders: Record<string, string>
): string {
  if (!placeholders || Object.keys(placeholders).length === 0) return text;
  let result = text;
  const entries = Object.entries(placeholders).sort(
    (a, b) => b[0].length - a[0].length
  );
  for (const [key, value] of entries) {
    if (!key) continue;
    result = result.split(key).join(value);
  }
  return result;
}
